import { c as _c } from "react/compiler-runtime";
import { getFormattedDateAndTime } from '@svs/nova-poolbetting/core/lib';
import type { DrawNumber } from '@svs/nova-poolbetting/schema/draw';
import { type DateTime, type Definition } from '@svs/nova-poolbetting/schema/frameworks';
import type { CouponSettings } from '@svs/nova-poolbetting/schema/sessionstore';
import type { EventNumber } from '@svs/nova-poolbetting/schema/sport';
import { cn } from '@svs/nova-poolbetting/ui/utils';
import { type ReactNode, Suspense } from 'react';
import { GameAnalysisContainer } from './GameAnalysisContainer';
import { GameAnalysisOutcomeFallback } from './GameAnalysisOutcomeFallback';
import { GameAnalysisTitle } from './GameAnalysisTitle';
type TipsinfoProps = {
  settings: CouponSettings;
  matchStartTime?: DateTime;
  drawNumber: DrawNumber;
  eventNumber: EventNumber;
  product: Definition['moduleEngine'];
  className?: string;
};

/**
 * Tipsinfo presentational component.
 *
 * @param root0 props
 * @param root0.settings coupon settings to display tipsinfo for
 * @param root0.matchStartTime time when match starts
 * @param root0.drawNumber draw number for game analysis
 * @param root0.eventNumber event number for game analysis
 * @param root0.product product to get game analysis for
 * @param root0.className optional class name for styling
 * @returns tipsinfo presentational component
 */
// eslint-disable-next-line complexity
export function Tipsinfo(t0) {
  const $ = _c(39);
  const {
    settings,
    matchStartTime,
    drawNumber,
    eventNumber,
    product,
    className
  } = t0;
  const {
    tipsinfo,
    gameAnalysis,
    outcomeLabel
  } = settings;
  let t1;
  if ($[0] !== outcomeLabel || $[1] !== tipsinfo) {
    t1 = tipsinfo.includes("SvenskaFolket") && outcomeLabel !== "LocalDistribution";
    $[0] = outcomeLabel;
    $[1] = tipsinfo;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const hasLocalDistribution = t1;
  let t2;
  if ($[3] !== tipsinfo) {
    t2 = tipsinfo.includes("Odds");
    $[3] = tipsinfo;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const hasOdds = t2;
  let t3;
  if ($[5] !== tipsinfo) {
    t3 = tipsinfo.includes("FavoriteOdds");
    $[5] = tipsinfo;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const hasFavoriteOdds = t3;
  let t4;
  if ($[7] !== tipsinfo) {
    t4 = tipsinfo.includes("StartOdds");
    $[7] = tipsinfo;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const hasStartOdds = t4;
  let t5;
  if ($[9] !== gameAnalysis) {
    t5 = gameAnalysis.includes("Tip");
    $[9] = gameAnalysis;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const hasGameAnalysisTip = t5;
  let t6;
  if ($[11] !== tipsinfo) {
    t6 = tipsinfo.includes("Matchstart");
    $[11] = tipsinfo;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const hasMatchStartTime = t6;
  const hasInfoToDisplay = hasLocalDistribution || hasOdds || hasFavoriteOdds || hasStartOdds || hasGameAnalysisTip || hasMatchStartTime && matchStartTime;
  if (!hasInfoToDisplay) {
    return;
  }
  let t7;
  if ($[13] !== className) {
    t7 = cn("text-layer-fg-muted mt-3 grid gap-1.5", className);
    $[13] = className;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== hasLocalDistribution) {
    t8 = hasLocalDistribution && <span>Svenska folket:</span>;
    $[15] = hasLocalDistribution;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== hasOdds) {
    t9 = hasOdds && <span>Aktuellt odds:</span>;
    $[17] = hasOdds;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== hasFavoriteOdds) {
    t10 = hasFavoriteOdds && <span>Favoritskap:</span>;
    $[19] = hasFavoriteOdds;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== hasStartOdds) {
    t11 = hasStartOdds && <span>Startodds:</span>;
    $[21] = hasStartOdds;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== drawNumber || $[24] !== eventNumber || $[25] !== hasGameAnalysisTip || $[26] !== product) {
    t12 = hasGameAnalysisTip && <Suspense fallback={<GameAnalysisOutcomeFallback />}><GameAnalysisContainer drawNumber={drawNumber} eventNumber={eventNumber} product={product} renderItem={_temp} /></Suspense>;
    $[23] = drawNumber;
    $[24] = eventNumber;
    $[25] = hasGameAnalysisTip;
    $[26] = product;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== hasMatchStartTime || $[29] !== matchStartTime) {
    t13 = hasMatchStartTime && matchStartTime && <span>Matchstart:{" "}{getFormattedDateAndTime(matchStartTime, {
        shouldCapitalizeFirstLetter: true,
        shouldUseNameOfDay: true,
        dateFormatOptions: "dayAsDateMonthAsText",
        formatOptions: "dateAndTime"
      })}</span>;
    $[28] = hasMatchStartTime;
    $[29] = matchStartTime;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t10 || $[32] !== t11 || $[33] !== t12 || $[34] !== t13 || $[35] !== t7 || $[36] !== t8 || $[37] !== t9) {
    t14 = <div aria-hidden={true} className={t7}>{t8}{t9}{t10}{t11}{t12}{t13}</div>;
    $[31] = t10;
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
    $[35] = t7;
    $[36] = t8;
    $[37] = t9;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  return t14;
}
function _temp(t0) {
  const {
    eventAnalyses
  } = t0;
  return <GameAnalysisTitle eventAnalyses={eventAnalyses} />;
}